/**
 * «Мои вопросы» (client-questions; ТЗ 26.10.2026, п.9.7; ответ заказчицы В29) — общее для клиента и
 * команды: статус вопроса, вложения сообщения (PDF/JPG/PNG, до 5 файлов, вместе до 25 МБ),
 * переписка и форма сообщения.
 */
import { useRef, useState, type FormEvent } from 'react'
import { Paperclip, X } from 'lucide-react'
import type { QuestionDetail, QuestionMessage, QuestionStatus } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'
import type { StatusRole } from '@/design-system/tokens/status'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Alert } from '@/design-system/components/feedback/alert'

/** Пределы — как на сервере. */
export const QUESTION_SUBJECT_MAX = 200
export const QUESTION_BODY_MAX = 4000
export const QUESTION_MAX_FILES = 5
export const QUESTION_MAX_TOTAL_BYTES = 25 * 1024 * 1024
const ACCEPT = '.pdf,.jpg,.jpeg,.png'
const ALLOWED_EXT = new Set(['pdf', 'jpg', 'jpeg', 'png'])

export const TEXTAREA_CLASS =
  'w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent'

/** Статус вопроса: подпись по стороне и цвет бейджа. */
const STATUS: Record<QuestionStatus, { client: string; team: string; role: StatusRole }> = {
  open: { client: 'Ждёт ответа', team: 'Ждёт ответа', role: 'warning' },
  answered: { client: 'Есть ответ', team: 'Отвечен', role: 'success' },
  closed: { client: 'Закрыт', team: 'Закрыт', role: 'neutral' },
}

export function questionStatusLabel(status: QuestionStatus, side: 'client' | 'team'): string {
  return STATUS[status][side]
}

export function QuestionStatusBadge({
  status,
  side,
}: {
  status: QuestionStatus
  side: 'client' | 'team'
}) {
  return <Badge status={STATUS[status].role}>{STATUS[status][side]}</Badge>
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`
}

/** Проверка вложений до отправки: тип, число, общий размер. Ошибка — текстом, иначе null. */
export function attachmentsError(files: File[]): string | null {
  if (files.length > QUESTION_MAX_FILES) return `Не больше ${QUESTION_MAX_FILES} файлов`
  const wrong = files.find((f) => !ALLOWED_EXT.has(f.name.split('.').pop()?.toLowerCase() ?? ''))
  if (wrong) return `${wrong.name}: можно прикрепить PDF, JPG или PNG`
  const total = files.reduce((sum, f) => sum + f.size, 0)
  if (total > QUESTION_MAX_TOTAL_BYTES) return 'Файлы вместе — не больше 25 МБ'
  return null
}

/**
 * Вложения сообщения: «Прикрепить файлы» (несколько сразу), список выбранных с «Убрать».
 * Управляемый: `files` — выбранные файлы.
 */
export function AttachmentsInput({
  files,
  onChange,
  disabled,
  error,
}: {
  files: File[]
  onChange: (files: File[]) => void
  disabled?: boolean
  error?: string | null
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        data-testid="question-files"
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? [])
          e.target.value = ''
          if (picked.length > 0) onChange([...files, ...picked])
        }}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={disabled || files.length >= QUESTION_MAX_FILES}
          leftIcon={<Paperclip className="size-4 stroke-current" aria-hidden />}
          onClick={() => inputRef.current?.click()}
        >
          Прикрепить файлы
        </Button>
        <span className="text-12 text-txt-tertiary">PDF, JPG или PNG — до 5 файлов, вместе до 25 МБ</span>
      </div>
      {files.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1" aria-label="Прикреплённые файлы">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-13 text-txt">
              <Paperclip className="size-3.5 shrink-0 stroke-current text-txt-tertiary" aria-hidden />
              <span className="min-w-0 truncate">{f.name}</span>
              <span className="shrink-0 text-txt-tertiary">{formatSize(f.size)}</span>
              <button
                type="button"
                aria-label={`Убрать файл ${f.name}`}
                disabled={disabled}
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                className="rounded-8 p-0.5 text-txt-secondary hover:bg-hover hover:text-txt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <X className="size-3.5 stroke-current" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {error ? <p className="mt-1 text-12 text-red">{error}</p> : null}
    </div>
  )
}

function MessageItem({ message, side }: { message: QuestionMessage; side: 'client' | 'team' }) {
  // Свои сообщения — слева на светлом фоне, ответы другой стороны — с акцентом.
  const own = side === 'client' ? message.fromClient : !message.fromClient
  return (
    <li
      className={cn(
        'rounded-12 border px-4 py-3',
        own ? 'border-line bg-white' : 'border-line bg-accent-bg',
      )}
    >
      <div className="text-12 text-txt-secondary">
        {message.authorName || (message.fromClient ? 'Клиент' : 'Центрика')} ·{' '}
        {formatDateTime(message.createdAt)}
      </div>
      <p className="mt-1 whitespace-pre-wrap break-words text-txt">{message.body}</p>
      {message.attachments.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1" aria-label="Вложения">
          {message.attachments.map((a) => (
            <li key={a.id} className="flex items-center gap-2 text-13">
              <Paperclip className="size-3.5 shrink-0 stroke-current text-txt-tertiary" aria-hidden />
              <a
                href={`/api/files/${a.id}/download`}
                className="min-w-0 truncate text-accent hover:underline"
                download={a.name}
              >
                {a.name}
              </a>
              <span className="shrink-0 text-txt-tertiary">{formatSize(a.size)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  )
}

/** Переписка по вопросу — от первого сообщения к последнему. */
export function QuestionThread({
  question,
  side,
}: {
  question: QuestionDetail
  side: 'client' | 'team'
}) {
  return (
    <ol className="flex flex-col gap-3" aria-label="Переписка">
      {question.messages.map((m) => (
        <MessageItem key={m.id} message={m} side={side} />
      ))}
    </ol>
  )
}

/**
 * Новое сообщение в вопрос: текст и вложения. `send` отправляет и отдаёт обновлённый вопрос;
 * ошибка остаётся над формой, введённое не теряется.
 */
export function QuestionReplyForm({
  label,
  submitLabel,
  placeholder,
  send,
  onSent,
}: {
  label: string
  submitLabel: string
  placeholder?: string
  send: (body: string, files: File[]) => Promise<QuestionDetail>
  onSent: (question: QuestionDetail) => void
}) {
  const [body, setBody] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [bodyError, setBodyError] = useState<string | null>(null)
  const filesError = attachmentsError(files)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!body.trim()) {
      setBodyError('Напишите текст')
      return
    }
    if (filesError) return
    setBusy(true)
    setError(null)
    setBodyError(null)
    try {
      const next = await send(body, files)
      setBody('')
      setFiles([])
      onSent(next)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось отправить сообщение'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      <Field label={label} error={bodyError ?? undefined} className="my-0">
        {(p) => (
          <textarea
            {...p}
            className={TEXTAREA_CLASS}
            value={body}
            maxLength={QUESTION_BODY_MAX}
            rows={4}
            disabled={busy}
            placeholder={placeholder}
            onChange={(e) => setBody(e.target.value)}
          />
        )}
      </Field>
      <div className="mt-3">
        <AttachmentsInput files={files} onChange={setFiles} disabled={busy} error={filesError} />
      </div>
      <Button type="submit" className="mt-4" disabled={busy || Boolean(filesError)}>
        {busy ? 'Отправка…' : submitLabel}
      </Button>
    </form>
  )
}
